import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

interface StepDefinition {
  readonly id: string;
  readonly label: string;
  /** Route segments (under `/onboarding/`) that should mark this step as the current one. */
  readonly matches: readonly string[];
}

const STEPS: readonly StepDefinition[] = [
  { id: 'account', label: 'Account', matches: ['', 'signup'] },
  { id: 'verify', label: 'Verify', matches: ['verify-email'] },
  { id: 'team', label: 'Size', matches: ['plan'] },
  // Payment happens on Stripe Checkout (external); no SPA route to match.
  // Renders as `upcoming` while on /plan and as `complete` once back on /confirmation.
  { id: 'payment', label: 'Payment', matches: [] },
  { id: 'done', label: 'Launch', matches: ['confirmation'] },
];

@Component({
  selector: 'ki-onboarding-stepper',
  standalone: true,
  templateUrl: './onboarding-stepper.component.html',
  styleUrl: './onboarding-stepper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingStepperComponent {
  constructor(private _router: Router) {
    this._currentUrl.set(this._router.url);

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((event) => {
        this._currentUrl.set((event as NavigationEnd).urlAfterRedirects);
      });
  }

  protected readonly steps = STEPS;

  private readonly _currentUrl = signal<string>('');

  protected readonly currentStepIndex = computed(() => {
    const url = this._currentUrl();
    const segments = this._segmentsAfterOnboarding(url);
    const firstSegment = segments[0] ?? '';

    // Match the most specific step first; fall back to the signup/account step.
    for (let i = STEPS.length - 1; i >= 0; i--) {
      if (STEPS[i].matches.includes(firstSegment)) {
        return i;
      }
    }
    return 0;
  });

  protected statusFor(index: number): 'complete' | 'current' | 'upcoming' {
    const current = this.currentStepIndex();
    if (index < current) return 'complete';
    if (index === current) return 'current';
    return 'upcoming';
  }

  /**
   * Strip query params/fragments and return the path segments that follow
   * `/onboarding/` (or an empty array when the user is on `/onboarding` itself).
   * Anything not under `/onboarding` returns an empty array, which means the
   * stepper falls back to step 1.
   */
  private _segmentsAfterOnboarding(url: string): string[] {
    const path = url.split('?')[0].split('#')[0];
    const parts = path.split('/').filter(Boolean);
    const onboardingIndex = parts.indexOf('onboarding');
    if (onboardingIndex === -1) {
      return [];
    }
    return parts.slice(onboardingIndex + 1);
  }
}
